import { Box } from '@mui/material';
import React from 'react';
import { useIntl } from 'react-intl';

import type { TemporalGroup } from '@verantos-dev/types-cohorts';

import { CollapsibleLogicNode } from './CollapsibleLogicNode';
import { LogicConnector } from './LogicConnector';
import { OccurrenceBadge } from './OccurrenceBadge';
import { TemporalDepthContext, getBadgeLabel, useTemporalDepth } from './TemporalDepthContext';
import { formatTemporalConnector } from '../../utils/analyticalVariable.utils';
import type { LogicNode } from '../../utils/analyticalVariable.utils';

interface BadgedNodeProps {
  label: string;
  depth: number;
  node: LogicNode;
  onChipClick: (name: string) => void;
  labelOverride?: string;
  'data-testid'?: string;
}

const BadgedNode = ({
  label,
  depth,
  node,
  onChipClick,
  labelOverride,
  'data-testid': testId,
}: BadgedNodeProps) => (
  <CollapsibleLogicNode
    node={node}
    onChipClick={onChipClick}
    badge={<OccurrenceBadge label={label} depth={depth} />}
    labelOverride={labelOverride}
    data-testid={testId}
  />
);

interface TemporalGroupRendererProps {
  group: TemporalGroup;
  onChipClick: (name: string) => void;
  occurrence?: number;
}

export const TemporalGroupRenderer = ({
  group,
  onChipClick,
  occurrence = 1,
}: TemporalGroupRendererProps) => {
  const intl = useIntl();
  const depth = useTemporalDepth();

  const connectorLabel = formatTemporalConnector(
    intl,
    group.relationship,
    group.minGap,
    group.maxGap,
    group.timeUnit,
    occurrence
  );

  const isSelfJoin = group.selfJoin && group.filters.length === 1;

  // When selfJoin is true with a single filter, render that filter twice
  // to visually represent the temporal self-join relationship
  const effectiveFilters = isSelfJoin ? [group.filters[0], group.filters[0]] : group.filters;

  // For self-join children, use the TG's own description
  const selfJoinChildLabel =
    isSelfJoin && group.description
      ? group.description[0].toUpperCase() + group.description.slice(1)
      : undefined;

  const items: React.ReactNode[] = [];

  effectiveFilters.forEach((filter, index) => {
    if (index > 0) {
      items.push(
        <LogicConnector
          key={`and-${index}`}
          label={intl.formatMessage({
            id: 'projects.analyticalVariables.logic.andOperator',
          })}
          emphasis="primary"
          data-testid="av-logic-temporal-and"
        />
      );
      items.push(
        <LogicConnector
          key={`rel-${index}`}
          label={connectorLabel}
          emphasis="temporal"
          data-testid="av-logic-temporal-relationship"
        />
      );
    }
    items.push(
      <BadgedNode
        key={`node-${index}`}
        label={getBadgeLabel(depth, index)}
        depth={depth}
        node={filter as LogicNode}
        onChipClick={onChipClick}
        labelOverride={selfJoinChildLabel}
        data-testid={`av-logic-temporal-filter-${index}`}
      />
    );
  });

  return (
    <TemporalDepthContext.Provider value={depth + 1}>
      <Box
        data-testid="av-logic-temporal-pair"
        sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
      >
        {items}
      </Box>
    </TemporalDepthContext.Provider>
  );
};
